import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';
import { deleteTrip, getTrip, getTrips } from '../../utils/API/trips';
import { TripForm } from './TripForm';
import { getSystem } from '../../utils/API/system';
import I3Modal from '../../components/Layout/I3Modal';
import I3ModalConfirmation from '../../components/Layout/I3ModalConfirmation';
import _ from 'lodash';
import { UserContext } from '../../context/UserContext';
import { PERMISSIONS } from '../../constants';
import { Unauthorized } from '../../components/Unauthorized';
import { currencyFormatter } from '../../components/Table/utility';
import { renderSpinner } from '../../utils/Utility';
import moment from 'moment';
import KilometersDataGrid from './KilometersDataGrid';

const fetchTripsData = async (setTrips, date, selectedUser) => {
  const tripsData = await getTrips(date, selectedUser);
  const orderedData = _.orderBy(tripsData, ['date'], ['asc']);
  setTrips(orderedData);
};

const Kilometers = ({
  showModal,
  handleShowModal,
  handleHideModal,
  showModalTableConfirm,
  handleShowModalTableConfirm,
  handleHideModalTableConfirm,
  month,
  isReportConfirmed,
  isReadOnly,
  isAdminViewingOther,
  selectedUser,
}) => {
  const [trips, setTrips] = useState([]);
  const [system, setSystem] = useState({});
  const [currentTrip, setCurrentTrip] = useState(false);
  const [isDeleteRunning, setIsDeleteRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const isMountedRef = useRef(true);

  const { user, hasClaim } = useContext(UserContext);
  const selectedUserFinal = selectedUser ?? user.username;

  const updateTrips = () => {
    if (!isMountedRef.current) {
      return Promise.resolve();
    }
    setIsLoading(true);
    const setTripsSafe = (next) => {
      if (isMountedRef.current) {
        setTrips(next);
      }
    };
    return fetchTripsData(setTripsSafe, month, selectedUserFinal).finally(
      () => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    );
  };

  const handleHideTripModal = () => {
    handleHideModal();
    setCurrentTrip(false);
  };

  useEffect(() => {
    getSystem().then((data) => {
      if (isMountedRef.current) {
        setSystem(data);
      }
    });
  }, []);

  useEffect(() => {
    updateTrips();
  }, [month, selectedUserFinal]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const edit = (id) => {
    const cachedTrip = trips.find((trip) => trip.id === id);
    if (cachedTrip) {
      setCurrentTrip(cachedTrip);
      handleShowModal();
      return;
    }

    setIsEditLoading(true);
    getTrip(id, selectedUserFinal)
      .then((trip) => {
        if (!isMountedRef.current) {
          return trip;
        }
        setCurrentTrip(trip);
        handleShowModal();
        return trip;
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsEditLoading(false);
        }
      });
  };

  const remove = (id) => {
    handleShowModalTableConfirm();
    /**
     *  in questo punto viene messo l'id all'interno di current trip.
     *  Non è corretto perchè formik si aspetta l'oggetto trip
     *  per ora abbiamo corretto l'errore resettando currentTrip sull'annulla della cancellazione.
     *  Ma va sistemato
     */
    setCurrentTrip(id);
  };

  const onConfirm = () => {
    setIsDeleteRunning(true);
    deleteTrip(currentTrip)
      .then(updateTrips)
      .finally(() => {
        setIsDeleteRunning(false);
        setCurrentTrip(false);
        handleHideModalTableConfirm();
      });
  };

  const onHideDeleteConfirmation = () => {
    setCurrentTrip(false);
    handleHideModalTableConfirm();
  };

  const tripSummary = () => {
    const item = trips.find((e) => e.id === currentTrip);
    if (!item) return '';
    const { date, description, kilometers, total_cost: cost } = item;
    return `${date} - ${description} - ${kilometers} km / ${currencyFormatter.format(
      cost
    )}`;
  };

  const canView = hasClaim(PERMISSIONS.TRIPS_KILOMETERS_VIEW) || hasClaim(PERMISSIONS.TRIPS_KILOMETERS_USER_SELECTION);
  const canEdit = hasClaim(PERMISSIONS.TRIPS_KILOMETERS_EDIT);
  const isReadOnlyEffective = isReadOnly || !canEdit;

  return canView ? (
    <>
      {renderSpinner(isEditLoading)}
      {isLoading ? (
        renderSpinner(true)
      ) : (
        <KilometersDataGrid
          isReportConfirmed={isReportConfirmed || !canEdit}
          data={trips}
          onDeleteClick={canEdit ? remove : undefined}
          onEditClick={edit}
          isAdminViewingOther={isAdminViewingOther || !canEdit}
        />
      )}

      <I3ModalConfirmation
        show={showModalTableConfirm.trip}
        onHide={onHideDeleteConfirmation}
        title="Cancella chilometri"
        questionTitle="Vuoi cancellare i chilometri?"
        questionText={tripSummary()}
        onConfirm={onConfirm}
        isLoading={isDeleteRunning}
      />

      {(() => {
        let modalTitle = 'Inserisci chilometri';
        if (currentTrip?.id) {
          modalTitle = isReadOnly
            ? 'Visualizza chilometri'
            : 'Modifica chilometri';
        }
        return (
          <I3Modal
            show={showModal}
            onHide={handleHideTripModal}
            title={modalTitle}
            className="I3Modal-add-km"
          >
            <TripForm
              readOnly={isReadOnlyEffective}
              close={handleHideTripModal}
              kmCost={system.kilometer_cost}
              currentTrip={currentTrip}
              updateTrips={updateTrips}
              month={month}
            />
          </I3Modal>
        );
      })()}
    </>
  ) : (
    <Unauthorized />
  );
};

Kilometers.defaultProps = {
  showModal: false,
  handleShowModal: () => {},
  handleHideModal: () => {},
  showModalTableConfirm: {},
  handleShowModalTableConfirm: () => {},
  handleHideModalTableConfirm: () => {},
  month: moment(),
  selectedUser: null,
  isReportConfirmed: false,
  isReadOnly: false,
  isAdminViewingOther: false,
};

Kilometers.propTypes = {
  showModal: PropTypes.bool,
  handleShowModal: PropTypes.func,
  handleHideModal: PropTypes.func,
  showModalTableConfirm: PropTypes.shape({
    trip: PropTypes.bool,
    expense: PropTypes.bool,
  }),
  handleShowModalTableConfirm: PropTypes.func,
  handleHideModalTableConfirm: PropTypes.func,
  month: PropTypes.instanceOf(moment),
  isReportConfirmed: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isAdminViewingOther: PropTypes.bool,
  selectedUser: PropTypes.string,
};

export default Kilometers;
